/**
 * Tiny formatting helpers for the Training Videos surface.
 *
 * vfmt          — seconds → "m:ss" (player chips, transcript timestamps)
 * fmtBytes      — int bytes → "1.4 MB" (file size on admin rows)
 * specColor     — specialisation id → swatch colour for chips/dots
 * statusLabel   — Video.status enum → human label
 */

export function vfmt(t: number): string {
  if (!Number.isFinite(t) || t < 0) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function fmtBytes(n: number): string {
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Map a specialisation id to a tag colour. Pulls from the OneView palette
 * (the same values bound in `frontend/tailwind.config.js`). Unknown specs
 * fall back to a neutral grey so chips always render.
 */
const SPEC_COLORS: Record<string, string> = {
  'spec-hr': '#3B5AA8',
  'spec-finance': '#172E57',
  'spec-finops': '#2F80ED',
  'spec-engineering': '#018236',
  'spec-security': '#BA0517',
  'spec-compliance': '#E97109',
  'spec-legal': '#828282',
  'spec-sales': '#8A3F00',
};

export function specColor(specId: string | undefined | null): string {
  if (!specId) return '#828282';
  return SPEC_COLORS[specId] ?? '#828282';
}

export type VideoStatus =
  | 'pending_upload'
  | 'transcoding'
  | 'transcribing'
  | 'summarising'
  | 'indexed'
  | 'failed';

export function statusLabel(status: VideoStatus): string {
  switch (status) {
    case 'pending_upload':
      return 'Uploading';
    case 'transcoding':
      return 'Extracting audio';
    case 'transcribing':
      return 'Transcribing';
    case 'summarising':
      return 'Summarising';
    case 'indexed':
      return 'Indexed';
    case 'failed':
      return 'Failed';
  }
}
